import {
	AdminAssignment,
	type GetDeliveryPerformanceQuery,
	ProductionStage,
	ProductionTurn,
	useGetDeliveryPerformanceQuery,
	useGetProductionOverviewQuery,
} from '@codezo/graphql';
import { ChevronRight, RefreshCw, Zap } from 'lucide-react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { cn } from '../../../lib/utils';
import { useCurrencyStore } from '../../../stores/currency';
import { money } from '../performance/performanceFormat';
import { InitialsAvatar } from '../../ui/initials-avatar';
import { SearchInput } from '../../ui/search-input';
import {
	DEADLINE_GROUPS,
	type GroupBy,
	LIFECYCLE,
	type ProductionOrder,
	STAGE_META,
	STAGE_ORDER,
	TONE_CHIP,
	deadlineBucket,
	deadlineLabel,
	sinceLabel,
} from './productionFormat';

const POLL_MS = 60_000;
const DUE_TONE = {
	danger: 'text-status-danger',
	warn: 'text-status-warn',
	muted: 'text-text-2',
} as const;
// Rows set their own 13px base: the page body is larger, and a dense table inherits it otherwise.
const GRID =
	'grid grid-cols-[20px_16px_minmax(240px,2.4fr)_176px_64px_96px_140px_104px_96px_22px] items-center gap-x-3 text-base';

/* ── Small pieces ── */

export function StagePie({ stage, size = 14 }: { stage: ProductionStage; size?: number }) {
	const meta = STAGE_META[stage];
	const dash = (meta.frac * 2 * Math.PI * 2.5).toFixed(2);
	return (
		<svg
			width={size}
			height={size}
			viewBox="0 0 14 14"
			role="img"
			aria-label={meta.label}
			className="shrink-0"
		>
			<circle
				cx="7"
				cy="7"
				r="6"
				fill="none"
				stroke={meta.color}
				strokeWidth="1.5"
				strokeDasharray={meta.dashed ? '2.2 1.6' : undefined}
			/>
			<circle
				cx="7"
				cy="7"
				r="2.5"
				fill="none"
				stroke={meta.color}
				strokeWidth="5"
				strokeDasharray={`${dash} 20`}
				transform="rotate(-90 7 7)"
			/>
		</svg>
	);
}

function Chip({ label, className }: { label: string; className: string }) {
	return (
		<span
			className={cn(
				'shrink-0 rounded px-1.5 py-0.5 text-xs font-medium whitespace-nowrap',
				className
			)}
		>
			{label}
		</span>
	);
}

function ToggleChip({
	on,
	onClick,
	children,
}: {
	on: boolean;
	onClick: () => void;
	children: React.ReactNode;
}) {
	return (
		<button
			type="button"
			aria-pressed={on}
			onClick={onClick}
			className={cn(
				'inline-flex h-[30px] items-center gap-2 rounded-lg border px-2.5 text-sm font-medium transition-colors',
				on
					? 'border-accent-line bg-accent-soft text-text-1'
					: 'border-line-1 bg-surface text-text-2 hover:border-line-3'
			)}
		>
			{children}
		</button>
	);
}

export function Segmented<T extends string>({
	value,
	onChange,
	options,
	label,
}: {
	value: T;
	onChange: (v: T) => void;
	options: [T, string][];
	label: string;
}) {
	return (
		<div
			role="group"
			aria-label={label}
			className="inline-flex max-w-full overflow-x-auto rounded-lg border border-line-1 bg-surface p-0.5"
		>
			{options.map(([v, text]) => (
				<button
					key={v}
					type="button"
					aria-pressed={value === v}
					onClick={() => onChange(v)}
					className={cn(
						'h-[26px] shrink-0 rounded-md px-3 text-xs font-medium whitespace-nowrap transition-colors',
						value === v
							? 'bg-surface-fill text-text-1 shadow-[0_0_0_1px_var(--color-line-2)]'
							: 'text-text-3 hover:text-text-1'
					)}
				>
					{text}
				</button>
			))}
		</div>
	);
}

/* ── Order row ── */

function OrderRow({ order, currency }: { order: ProductionOrder; currency: string }) {
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const due = deadlineLabel(order.deadline);
	const stepIndex = LIFECYCLE.findIndex((s) => s.step === order.lifecycleStep);
	const owner = order.order.orderQuote?.adminUsers?.[0];
	const ownerLabel = owner ? `${owner.firstName} ${owner.lastName}`.trim() : null;
	const stageColor = STAGE_META[order.stage].color;
	const multi = order.products.length > 1;
	const chips = [...order.risks, ...order.flags];

	return (
		<>
			<div
				className={cn(
					GRID,
					'h-14 cursor-pointer border-b border-line-1 px-4 transition-colors hover:bg-surface-hover'
				)}
				onClick={() => navigate(`/orders/${order.id}`)}
				role="link"
				tabIndex={0}
				onKeyDown={(e) => e.key === 'Enter' && navigate(`/orders/${order.id}`)}
			>
				<span>
					{multi && (
						<button
							type="button"
							aria-expanded={open}
							aria-label={open ? 'Hide products' : 'Show products'}
							onClick={(e) => {
								e.stopPropagation();
								setOpen((v) => !v);
							}}
							className="flex size-5 items-center justify-center rounded text-text-3 hover:bg-surface-fill hover:text-text-1"
						>
							<ChevronRight
								size={12}
								className={cn('transition-transform', open && 'rotate-90')}
							/>
						</button>
					)}
				</span>
				<StagePie stage={order.stage} />
				{/* Name over client and number, as in the Orders list. One chip — the most serious —
				    so long real names keep their room; the rest are counted. */}
				<span className="flex min-w-0 flex-col gap-0.5">
					<span className="flex min-w-0 items-center gap-2">
						<span className="truncate font-medium text-text-1">{order.order.name}</span>
						{chips[0] && (
							<Chip label={chips[0].label} className={TONE_CHIP[chips[0].tone]} />
						)}
						{chips.length > 1 && (
							<span
								className="shrink-0 text-xs text-text-4"
								title={chips
									.slice(1)
									.map((c) => c.label)
									.join(', ')}
							>
								+{chips.length - 1}
							</span>
						)}
					</span>
					<span className="flex min-w-0 items-center gap-1.5 text-xs text-text-4">
						<span className="truncate">{order.order.organization?.name ?? '—'}</span>
						<span aria-hidden="true">·</span>
						<span className="truncate font-mono">{order.order.orderNumber}</span>
					</span>
				</span>
				<span className="flex items-center gap-2" title={LIFECYCLE[stepIndex]?.label}>
					<span className="flex gap-0.5" aria-hidden="true">
						{LIFECYCLE.map((s, i) => (
							<span
								key={s.step}
								className="h-1 w-2 rounded-[1px]"
								style={{
									background:
										i < stepIndex
											? 'var(--color-text-2)'
											: i === stepIndex
												? stageColor
												: 'var(--color-line-2)',
								}}
							/>
						))}
					</span>
					<span className="truncate text-xs text-text-3">
						{LIFECYCLE[stepIndex]?.label}
					</span>
				</span>
				<span>
					<Chip
						label={order.turn === ProductionTurn.Client ? 'Client' : 'Studio'}
						className={
							order.turn === ProductionTurn.Client
								? 'bg-status-warn-soft text-status-warn'
								: 'bg-surface-fill text-text-2'
						}
					/>
				</span>
				<span className="flex items-center gap-1.5 whitespace-nowrap text-sm text-text-2">
					{order.express && (
						<Zap size={10} className="fill-accent text-accent" aria-label="Express" />
					)}
					{order.shipping ?? '—'}
				</span>
				<span className="truncate text-sm text-text-3">
					{sinceLabel(order.sinceLabel, order.since)}
				</span>
				<span className="text-right text-sm tabular-nums text-text-2">
					{money(order.value, currency)}
				</span>
				<span
					className={cn(
						'text-right text-sm tabular-nums whitespace-nowrap',
						DUE_TONE[due.tone]
					)}
				>
					{due.text}
				</span>
				<span title={ownerLabel ?? 'Unassigned'}>
					{ownerLabel && <InitialsAvatar name={ownerLabel} size={20} />}
				</span>
			</div>
			{open && (
				<div className="border-b border-line-1 bg-surface-sunken py-1.5 pr-4 pl-16 text-base">
					{order.products.map((p) => (
						<div
							key={p.orderItemId}
							className="grid h-9 grid-cols-[minmax(160px,1.2fr)_80px_200px_minmax(220px,2fr)] items-center gap-3 border-b border-dashed border-line-1 last:border-b-0"
						>
							<span className="truncate text-text-1">{p.name}</span>
							<span className="tabular-nums text-sm text-text-3">
								{p.quantity} pcs
							</span>
							<span>
								<Chip label={p.note} className={TONE_CHIP[p.tone]} />
							</span>
							<span className="truncate text-sm text-text-2">
								{p.tracks.length ? p.tracks.join('  ·  ') : 'No shipment yet'}
							</span>
						</div>
					))}
				</div>
			)}
		</>
	);
}

/* ── Delivery stats ── */

type DeliveryPerformance = GetDeliveryPerformanceQuery['getDeliveryPerformance'];

/** ISO week number of a Monday-start week (weekStart is that Monday, midnight UTC). */
function isoWeek(iso: string): number {
	const d = new Date(iso);
	const t = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 3));
	const firstThursday = new Date(Date.UTC(t.getUTCFullYear(), 0, 4));
	return 1 + Math.round((t.getTime() - firstThursday.getTime()) / (7 * 864e5));
}

/**
 * Shipments delivered per week, on time and late stacked. Counts rather than a weekly percentage:
 * at a few deliveries a week the rate swings between 0 and 100% and a line reads as a trend that
 * isn't there, while a column of two blocks says exactly what happened.
 */
const shipments = (n: number) => `${n} ${n === 1 ? 'shipment' : 'shipments'}`;

function OnTimeCard({ perf }: { perf?: DeliveryPerformance }) {
	const weeks = perf?.weeks ?? [];
	const max = Math.max(0, ...weeks.map((w) => w.delivered));
	const top = Math.max(2, max <= 10 ? max : Math.ceil(max / 5) * 5);
	const last = weeks.length - 1;

	return (
		<section className="overflow-hidden rounded-xl border border-line-1 bg-surface">
			<div className="flex h-11 items-center gap-2 border-b border-line-1 px-4">
				<h2 className="text-sm font-medium uppercase tracking-[0.04em] text-text-1">
					On-time delivery
				</h2>
				<span className="text-sm text-text-4">last 12 weeks</span>
				<span className="flex-1" />
				<span className="hidden items-center gap-3 text-xs text-text-3 sm:inline-flex">
					<span className="inline-flex items-center gap-1.5">
						<span className="size-2 rounded-[2px] bg-accent" />
						On time
					</span>
					<span className="inline-flex items-center gap-1.5">
						<span className="size-2 rounded-[2px] bg-status-danger" />
						Late
					</span>
				</span>
				<span className="ml-2 text-sm tabular-nums text-text-1">
					{perf?.onTimeRate != null ? `${perf.onTimeRate}%` : '—'}
				</span>
			</div>
			<div className="px-4 pt-5">
				{max > 0 ? (
					<>
						<div className="relative ml-7 h-[120px]">
							<span className="absolute inset-x-0 top-0 h-px bg-line-1" />
							<span className="absolute inset-x-0 bottom-0 h-px bg-line-2" />
							<span className="absolute top-0 -left-7 -translate-y-1/2 font-mono text-2xs text-text-4">
								{top}
							</span>
							<span className="absolute bottom-0 -left-7 translate-y-1/2 font-mono text-2xs text-text-4">
								0
							</span>
							<div className="absolute inset-0 flex items-end">
								{weeks.map((w) => {
									const late = w.delivered - w.onTime;
									return (
										<div
											key={w.weekStart}
											className="flex h-full min-w-0 flex-1 flex-col justify-end px-1.5 sm:px-3"
											title={
												w.delivered
													? `Week ${isoWeek(w.weekStart)}: ${w.delivered} delivered, ${late} late`
													: `Week ${isoWeek(w.weekStart)}: no deliveries`
											}
										>
											{late > 0 && (
												<span
													className="block rounded-t-[2px] bg-status-danger"
													style={{ height: `${(late / top) * 100}%` }}
												/>
											)}
											{w.onTime > 0 && (
												<span
													className={cn(
														'block bg-accent',
														late === 0 && 'rounded-t-[2px]'
													)}
													style={{ height: `${(w.onTime / top) * 100}%` }}
												/>
											)}
										</div>
									);
								})}
							</div>
						</div>
						<div className="mt-2 ml-7 flex">
							{weeks.map((w, i) => (
								<span
									key={w.weekStart}
									className={cn(
										'min-w-0 flex-1 text-center font-mono text-2xs whitespace-nowrap',
										i === last ? 'text-text-1' : 'text-text-4'
									)}
								>
									{i % 3 === 0 || i === last ? `W${isoWeek(w.weekStart)}` : ''}
								</span>
							))}
						</div>
					</>
				) : (
					<div className="flex h-[140px] items-center justify-center text-sm text-text-3">
						No deliveries with an ETA in the last 12 weeks yet.
					</div>
				)}
			</div>
			<div className="mt-4 grid grid-cols-3 border-t border-line-1">
				{[
					['Delivered', perf ? shipments(perf.delivered) : '—'],
					[
						'Late',
						perf ? shipments(perf.late) : '—',
						perf?.late ? 'text-status-warn' : undefined,
					],
					[
						'Avg. delay when late',
						perf?.averageDelayDays != null ? `${perf.averageDelayDays} days` : '—',
					],
				].map(([label, value, tone], i) => (
					<div
						key={label}
						className={cn(
							'flex flex-col gap-1 px-4 py-3',
							i > 0 && 'border-l border-line-1'
						)}
					>
						<span className="text-2xs font-medium uppercase tracking-[0.12em] text-text-4">
							{label}
						</span>
						<span className={cn('text-lg tabular-nums', tone ?? 'text-text-1')}>
							{value}
						</span>
					</div>
				))}
			</div>
		</section>
	);
}

function ManufacturersCard({ perf }: { perf?: DeliveryPerformance }) {
	const rows = perf?.manufacturers ?? [];
	const rateTone = (r: number | null | undefined) =>
		r == null
			? 'text-text-3'
			: r >= 90
				? 'text-status-success'
				: r >= 80
					? 'text-status-warn'
					: 'text-status-danger';
	const rateBar = (r: number | null | undefined) =>
		r == null
			? 'bg-line-2'
			: r >= 90
				? 'bg-status-success'
				: r >= 80
					? 'bg-status-warn'
					: 'bg-status-danger';

	return (
		<section className="overflow-hidden rounded-xl border border-line-1 bg-surface">
			<div className="flex h-11 items-center border-b border-line-1 px-4">
				<h2 className="text-sm font-medium uppercase tracking-[0.04em] text-text-1">
					Manufacturers
				</h2>
				<span className="flex-1" />
				<Link to="/vetters" className="text-xs text-text-3 hover:text-text-1">
					All vetters →
				</Link>
			</div>
			<div className="grid h-8 grid-cols-[minmax(0,1fr)_72px_132px_64px] items-center gap-4 border-b border-line-1 px-4 text-xs text-text-4">
				<span>Manufacturer</span>
				<span>Active</span>
				<span className="text-right">On time</span>
				<span className="text-right">Avg. delay</span>
			</div>
			{rows.length === 0 ? (
				<div className="px-4 py-8 text-center text-sm text-text-3">
					No supplier deliveries yet.
				</div>
			) : (
				rows.map((m) => (
					<Link
						key={m.vetterEntityId}
						to={`/vetters/${m.vetterEntityId}`}
						className="grid h-[42px] grid-cols-[minmax(0,1fr)_72px_132px_64px] items-center gap-4 border-b border-line-1 px-4 text-base transition-colors last:border-b-0 hover:bg-surface-hover"
					>
						<span className="truncate font-medium text-text-1">
							{m.name ?? 'Unknown supplier'}
						</span>
						<span className="tabular-nums text-sm text-text-3">
							{m.activeOrders} {m.activeOrders === 1 ? 'order' : 'orders'}
						</span>
						<span className="flex items-center gap-2.5">
							<span className="h-1 flex-1 overflow-hidden rounded-full bg-line-1">
								<span
									className={cn(
										'block h-full rounded-full',
										rateBar(m.onTimeRate)
									)}
									style={{ width: `${m.onTimeRate ?? 0}%` }}
								/>
							</span>
							<span
								className={cn(
									'w-9 text-right tabular-nums text-sm',
									rateTone(m.onTimeRate)
								)}
							>
								{m.onTimeRate != null ? `${m.onTimeRate}%` : '—'}
							</span>
						</span>
						<span
							className={cn(
								'text-right tabular-nums text-sm',
								m.averageDelayDays == null
									? 'text-text-3'
									: m.averageDelayDays >= 3
										? 'text-status-danger'
										: m.averageDelayDays >= 2
											? 'text-status-warn'
											: 'text-text-2'
							)}
						>
							{m.averageDelayDays != null ? `${m.averageDelayDays} d` : '—'}
						</span>
					</Link>
				))
			)}
		</section>
	);
}

/* ── Tab ── */

export function ProductionTab({
	assignment,
	onCounts,
}: {
	assignment: AdminAssignment;
	onCounts: (counts: { everyone: number; mine: number; unassigned: number }) => void;
}) {
	const { selectedCurrency } = useCurrencyStore();
	const { data, loading, error, refetch } = useGetProductionOverviewQuery({
		variables: { assignment, currency: selectedCurrency },
		fetchPolicy: 'no-cache',
		pollInterval: POLL_MS,
	});
	const { data: deliveryData } = useGetDeliveryPerformanceQuery({
		variables: { weeks: 12 },
		fetchPolicy: 'no-cache',
	});
	const perf = deliveryData?.getDeliveryPerformance;
	const [expressOnly, setExpressOnly] = useState(false);
	const [riskOnly, setRiskOnly] = useState(false);
	const [search, setSearch] = useState('');
	const [stageFilter, setStageFilter] = useState<ProductionStage | null>(null);
	const [groupBy, setGroupBy] = useState<GroupBy>('stage');

	const overview = data?.getProductionOverview;
	useEffect(() => {
		if (overview) {
			onCounts({
				everyone: overview.everyoneCount,
				mine: overview.mineCount,
				unassigned: overview.unassignedCount,
			});
		}
	}, [overview, onCounts]);
	const currency = overview?.currency ?? selectedCurrency;
	const all = useMemo(() => overview?.orders ?? [], [overview]);
	const riskCount = all.filter((o) => o.risks.length > 0).length;

	const needle = search.trim().toLowerCase();
	// Client, product or order number — what an admin has in hand when someone calls.
	const matches = (o: ProductionOrder) =>
		!needle ||
		[
			o.order.name,
			o.order.orderNumber,
			o.order.organization?.name,
			...o.products.map((p) => p.name),
		].some((v) => v?.toLowerCase().includes(needle));
	const shown = all.filter(
		(o) =>
			matches(o) &&
			(!expressOnly || o.express) &&
			(!riskOnly || o.risks.length > 0) &&
			(!stageFilter || o.stage === stageFilter)
	);

	type Group = {
		key: string;
		label: string;
		stage?: ProductionStage;
		danger?: boolean;
		rows: ProductionOrder[];
	};
	const groups = useMemo((): Group[] => {
		if (groupBy === 'turn') {
			return [
				{
					key: 'studio',
					label: 'Waiting on studio',
					rows: shown.filter((o) => o.turn === ProductionTurn.Studio),
				},
				{
					key: 'client',
					label: 'Waiting on client',
					rows: shown.filter((o) => o.turn === ProductionTurn.Client),
				},
			];
		}
		if (groupBy === 'deadline') {
			return DEADLINE_GROUPS.map((g) => ({
				key: g.key,
				label: g.label,
				danger: 'danger' in g,
				rows: shown.filter((o) => deadlineBucket(o.deadline) === g.key),
			}));
		}
		return STAGE_ORDER.map((stage) => ({
			key: stage,
			label: STAGE_META[stage].label,
			stage,
			rows: shown.filter((o) => o.stage === stage),
		}));
	}, [shown, groupBy]);

	const riskLine = all
		.filter((o) => o.risks.length > 0)
		.map(
			(o) =>
				`${o.risks[0].label.toLowerCase()} (${o.order.organization?.name ?? o.order.name})`
		);

	return (
		<div className="flex flex-col gap-4">
			{/* Toolbar */}
			<div className="flex flex-wrap items-center gap-2">
				<SearchInput
					value={search}
					onChange={setSearch}
					placeholder="Client, product or order no."
					className="w-full sm:w-[240px] [&_input]:h-[30px]"
				/>
				<ToggleChip on={expressOnly} onClick={() => setExpressOnly((v) => !v)}>
					<Zap size={10} className="fill-accent text-accent" /> Express only
				</ToggleChip>
				<ToggleChip on={riskOnly} onClick={() => setRiskOnly((v) => !v)}>
					<span className="size-1.5 rounded-full bg-status-danger" /> At risk
					<span className="tabular-nums text-text-4">{riskCount}</span>
				</ToggleChip>
				{(expressOnly || riskOnly || stageFilter || needle) && (
					<button
						type="button"
						onClick={() => {
							setExpressOnly(false);
							setRiskOnly(false);
							setStageFilter(null);
							setSearch('');
						}}
						className="px-1.5 text-sm text-text-3 hover:text-text-1"
					>
						Clear
					</button>
				)}
				<span className="flex-1" />
				<span className="text-sm text-text-4">Group by</span>
				<Segmented
					label="Group by"
					value={groupBy}
					onChange={setGroupBy}
					options={[
						['stage', 'Stage'],
						['turn', 'Waiting on'],
						['deadline', 'Deadline'],
					]}
				/>
			</div>

			{/* Stage flow */}
			<section
				aria-label="Order pipeline"
				className="overflow-hidden rounded-xl border border-line-1 bg-surface"
			>
				<div className="overflow-x-auto">
					<div className="flex min-w-[880px]">
						{(
							overview?.stages ??
							STAGE_ORDER.map((stage) => ({
								stage,
								count: 0,
								value: 0,
								studio: 0,
								client: 0,
								atRisk: 0,
							}))
						).map((s, i) => (
							<Fragment key={s.stage}>
								{i > 0 && (
									<span
										aria-hidden="true"
										className="z-[1] -mx-2.5 flex w-5 shrink-0 items-center justify-center"
									>
										<span className="flex size-5 items-center justify-center rounded-full border border-line-2 bg-surface text-text-4">
											<ChevronRight size={11} />
										</span>
									</span>
								)}
								<button
									type="button"
									aria-pressed={stageFilter === s.stage}
									onClick={() =>
										setStageFilter((cur) => (cur === s.stage ? null : s.stage))
									}
									className={cn(
										'flex min-w-0 flex-1 flex-col gap-2 px-[18px] py-4 text-left transition-colors hover:bg-surface-hover',
										i > 0 && 'border-l border-line-1',
										stageFilter === s.stage && 'bg-surface-fill'
									)}
								>
									<span className="flex items-center gap-2 text-sm whitespace-nowrap text-text-2">
										<StagePie stage={s.stage} />
										{STAGE_META[s.stage].label}
										{s.atRisk > 0 && (
											<Chip
												label={`${s.atRisk} at risk`}
												className="ml-auto bg-status-danger-soft text-status-danger"
											/>
										)}
									</span>
									<span className="flex items-baseline gap-2">
										<span className="text-[28px] leading-none -tracking-[0.03em] tabular-nums text-text-1">
											{loading && !overview ? '–' : s.count}
										</span>
										<span className="text-sm tabular-nums text-text-3">
											{money(s.value, currency)}
										</span>
									</span>
									<span className="text-xs tabular-nums whitespace-nowrap text-text-3">
										Studio {s.studio} <span className="text-text-4">·</span>{' '}
										<span className="text-status-warn">Client {s.client}</span>
									</span>
								</button>
							</Fragment>
						))}
					</div>
				</div>
				<div className="flex min-h-10 items-center gap-2 border-t border-line-1 px-4 py-2.5 text-xs text-text-3">
					<span
						className={cn(
							'size-1.5 shrink-0 rounded-full',
							riskLine.length ? 'bg-status-danger' : 'bg-status-success'
						)}
					/>
					<span>
						{riskLine.length
							? `${riskLine.length} at risk: ${riskLine.join(', ')}.`
							: 'Nothing at risk.'}
					</span>
				</div>
			</section>

			{/* Orders */}
			<section
				aria-labelledby="orders-title"
				className="overflow-hidden rounded-xl border border-line-1 bg-surface"
			>
				<div className="flex h-11 items-center gap-2 border-b border-line-1 px-4">
					<h2
						id="orders-title"
						className="text-sm font-medium uppercase tracking-[0.04em] text-text-1"
					>
						Orders in progress
					</h2>
					<span className="text-sm tabular-nums text-text-4">
						{shown.length} of {all.length}
					</span>
					<span className="flex-1" />
					<Link to="/orders" className="text-xs text-text-3 hover:text-text-1">
						Open in Orders →
					</Link>
				</div>
				{error && !overview ? (
					<div className="flex flex-col items-center gap-3 py-12 text-center">
						<span className="text-base text-text-2">Couldn&rsquo;t load orders.</span>
						<button
							type="button"
							onClick={() => refetch()}
							className="inline-flex items-center gap-1.5 rounded-lg border border-line-3 px-3 py-1.5 text-sm text-text-1 hover:bg-surface-hover"
						>
							<RefreshCw size={13} /> Try again
						</button>
					</div>
				) : (
					<div className="overflow-x-auto">
						<div className="min-w-[1080px]">
							<div
								className={cn(
									GRID,
									'h-8 border-b border-line-1 px-4 text-xs text-text-4'
								)}
							>
								<span />
								<span />
								<span>Order</span>
								<span>Lifecycle</span>
								<span>Turn</span>
								<span>Shipping</span>
								<span>Since</span>
								<span className="text-right">Value</span>
								<span className="text-right">Deadline</span>
								<span />
							</div>
							{loading && !overview
								? Array.from({ length: 5 }).map((_, i) => (
										<div
											key={i}
											className="flex h-14 items-center gap-3 border-b border-line-1 px-4"
										>
											<span className="h-3 w-1/4 animate-pulse rounded bg-surface-fill" />
										</div>
									))
								: groups
										.filter((g) => g.rows.length > 0)
										.map((g) => (
											<div key={g.key}>
												<div className="flex h-8 items-center gap-2 border-b border-line-1 bg-surface-hover px-4 text-sm font-medium">
													{'stage' in g && g.stage && (
														<StagePie stage={g.stage} />
													)}
													<span
														className={
															'danger' in g && g.danger
																? 'text-status-danger'
																: 'text-text-2'
														}
													>
														{g.label}
													</span>
													<span className="font-normal tabular-nums text-text-4">
														{g.rows.length}
													</span>
													<span className="flex-1" />
													<span className="text-sm font-normal tabular-nums text-text-4">
														{money(
															g.rows.reduce(
																(sum, o) => sum + o.value,
																0
															),
															currency
														)}
													</span>
												</div>
												{g.rows.map((o) => (
													<OrderRow
														key={o.id}
														order={o}
														currency={currency}
													/>
												))}
											</div>
										))}
							{!loading && shown.length === 0 && (
								<div className="py-12 text-center text-base text-text-3">
									{all.length === 0
										? 'No orders in progress.'
										: 'No orders match these filters.'}
								</div>
							)}
						</div>
					</div>
				)}
			</section>

			<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
				<OnTimeCard perf={perf} />
				<ManufacturersCard perf={perf} />
			</div>
		</div>
	);
}
